import ReactModal from "react-modal";
import { BoxModal } from "../../../components/boxModal";

import './styles.css'
import { useEffect, useState } from "react";
import { Input } from "../../../components/Input";
import { Controller, useForm } from "react-hook-form";
import Select from 'react-select';
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiV1 } from "../../../lib/axios";
import { LiaLockOpenSolid, LiaLockSolid, LiaPenSolid, LiaTrashSolid } from "react-icons/lia";
import { useAuth } from "../../../hooks/useAuth";
import { sendTransactionMfa } from "../../../services/mfa.service";

const updateAccessFormSchema = z.object({
  name: z.string(),
  mail: z
    .string()
    .refine(
      (val) => val === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val),
      { message: 'Email inválido' }
    ),
  phone: z.string(),
  newPassword: z.string().optional().refine((value) => {
    if (!value || value.length === 0) return true;

    return (
      value.length >= 8 &&
      /[A-Z]/.test(value) &&
      /[a-z]/.test(value) &&
      /[0-9]/.test(value) &&
      /[^A-Za-z0-9]/.test(value)
    );
  }, {
    message: 'A senha deve conter pelo menos 8 caracteres, incluindo uma letra maiúscula, uma minúscula, um número e um caractere especial',
  }),
  permissions: z.array(z.string()).min(1, { message: 'Selecione pelo menos uma permissão' }),
})

export function UpdateAccessModal({ isOpen, selectedAccess, setUpdateAccessModal, updateCompanyUser, updateCompanyUserStatus, deleteCompanyUser }) {
  const [isEditing, setIsEditing] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [formDataPreview, setFormDataPreview] = useState(null);
  const [step, setStep] = useState('form');
  const [mfaCode, setMfaCode] = useState('');

  const { user } = useAuth();

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(updateAccessFormSchema),
    defaultValues: {
      name: selectedAccess?.name || '',
      phone: selectedAccess?.phone || '',
      mail: selectedAccess?.mail || '',
      newPassword: '',
      permissions: selectedAccess?.companyUserPermissions?.map(p => p.id) || []
    }
  })

  const companyUserStatusMap = {
    'active': 'Ativo',
    'inactive': 'Bloqueado',
  }

  const toggleEdit = () => {
    setIsEditing(!isEditing)
  }

  const handleStatus = () => {
    updateCompanyUserStatus({
      id: selectedAccess?.id,
      status: selectedAccess?.status === 'active' ? 'inactive' : 'active'
    })
  }

  const handleFormSubmit = (data) => {
    setFormDataPreview(data);
    setStep('preview');
    sendTransactionMfa();
  };

  const confirmMfaAndSave = () => {
    updateCompanyUser({
      ...formDataPreview,
      id: selectedAccess?.id,
      otp: mfaCode,
    });
    setStep('form');
  }

  const getPermissions = async () => {
    const result = await apiV1.get('/permissions');
    setPermissions(result.data.permissions.map(permission =>
    ({
      value: permission.id,
      label: permission.label
    })
    ));
  }

  useEffect(() => {
    if (selectedAccess) {
      setValue('name', selectedAccess.name || '');
      setValue('phone', selectedAccess.phone || '');
      setValue('mail', selectedAccess.mail || '');
      setValue('permissions', selectedAccess.permissions?.map(p => p.id) || []);
    }
  }, [selectedAccess]);

  useEffect(() => {
    if (selectedAccess && permissions.length > 0) {
      const selectedPermissionIds = selectedAccess.companyUserPermissions?.map(p => p.id) || [];
      setValue('permissions', selectedPermissionIds);
    }
  }, [selectedAccess, permissions]);

  useEffect(() => {
    getPermissions();
  }, []);
  return (
    <ReactModal isOpen={isOpen} className="change-modal" overlayClassName="overlay-change-password-modal">
      <BoxModal title={isEditing ? 'Editar acesso' : 'Gerenciar acesso'} maxWidth={680} close={() => setUpdateAccessModal(false)}>
        {
          isEditing &&
          <>
            {
              step === 'form' &&
              (
                <form onSubmit={handleSubmit(handleFormSubmit)}>
                  <div className="d-flex flex-column mb-3">
                    <Input
                      type="text"
                      label="Name"
                      register={register('name')}
                      isRequired={true}
                    />
                    {errors.name && <span>{errors.name.message}</span>}
                  </div>
                  <div className="d-flex flex-column mb-3">
                    <Input
                      type="text"
                      label="Telefone"
                      maxLength={11}
                      onlyNumber
                      register={register('phone')}
                      isRequired={true}
                    />
                    {errors.phone && <span>{errors.phone.message}</span>}
                  </div>
                  <div className="d-flex flex-column mb-3">
                    <Input
                      type="text"
                      label="Email"
                      register={register('mail')}
                      isRequired={true}
                    />
                    {errors.mail && <span>{errors.mail.message}</span>}
                  </div>
                  <div className="d-flex flex-column mb-3">
                    <Input
                      type="password"
                      label="Senha"
                      register={register('newPassword')}
                    />
                    {errors.newPassword && <span>{errors.newPassword.message}</span>}
                  </div>
                  <div className="input-group d-flex flex-column mb-3">
                    <label className="form-label">Permissões <span className="text-danger">*</span></label>

                    <Controller
                      name="permissions"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={permissions}
                          isMulti
                          placeholder="Selecione permissões..."
                          classNamePrefix="react-select"
                          onChange={(selectedOptions) => {
                            field.onChange(selectedOptions.map((opt) => opt.value));
                          }}
                          value={permissions?.filter(opt => field.value?.includes(opt.value))}
                        />
                      )}
                    />
                    {errors.permissions && <span className="text-danger">{errors.permissions.message}</span>}
                  </div>
                  <div className="button-group mt-4 d-flex justify-content-between">
                    <button className="btn btn-success col-5" type="submit">Confirmar</button>
                    <button className="btn btn-outline-secondary col-5" type="button" onClick={toggleEdit}>Cancelar</button>
                  </div>
                </form>
              )
            }
            {
              step === 'preview' && (
                <div className="mfa-confirmation-step">
                  <h5>Confirme a alteração</h5>
                  <p>Enviamos um código por SMS para {user?.phoneNumber}</p>
                  <div className="comparison">
                    <div><strong>Nome:</strong> {selectedAccess.name} → {formDataPreview.name}</div>
                    <div><strong>Telefone:</strong> {selectedAccess.phone} → {formDataPreview.phone}</div>
                    <div><strong>Email:</strong> {selectedAccess.mail} → {formDataPreview.mail}</div>
                  </div>
                  <div className="d-flex flex-column my-3">
                    <label>Código MFA</label>
                    <input
                      className="form-control"
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value)}
                      maxLength={6}
                      placeholder="Digite o código recebido"
                    />
                  </div>
                  <div className="d-flex justify-content-between mt-3">
                    <button className="btn btn-outline-secondary col-5" onClick={() => setStep('form')}>Voltar</button>
                    <button className="btn btn-success col-5" onClick={confirmMfaAndSave}>Confirmar</button>
                  </div>
                </div>
              )
            }
          </>
        }
        {
          !isEditing &&
          <>
            <div className="modal-content border-0">
              <div className="d-flex justify-content-between align-items-center flex-wrap p-3 shadow-lg">
                <div className="mb-2">
                  <div className="fw-semibold">Nome: {selectedAccess?.name}</div>
                  <div className="text-muted small">Telefone: {selectedAccess?.phone}</div>
                  <div className="text-muted small">Email: {selectedAccess?.mail}</div>
                  <div className={`mt-1 small ${selectedAccess?.status === 'active' ? 'text-success' : 'text-danger'}`}>
                    <i className="bi bi-circle-fill me-1"></i>Status: {companyUserStatusMap[selectedAccess?.status]}
                  </div>
                </div>
              </div>
            </div>
            <div className="button-group mt-4 d-flex justify-content-between">
              <button className="btn btn-success col-md-3 d-flex flex-row align-items-center justify-content-center gap-2" onClick={() => toggleEdit(isEditing)}>
                <LiaPenSolid />
                Alterar
              </button>
              <button className="btn btn-success col-md-3 d-flex flex-row align-items-center justify-content-center gap-2" onClick={() => handleStatus()}>
                {selectedAccess?.status === 'active' ? <LiaLockSolid /> : <LiaLockOpenSolid />}
                {selectedAccess?.status === 'active' ? 'Bloquear' : 'Desbloquear'}
              </button>
              <button className="btn btn-danger col-md-3 d-flex flex-row align-items-center justify-content-center gap-2" onClick={() => deleteCompanyUser(selectedAccess)}>
                <LiaTrashSolid />
                Excluir
              </button>
            </div>
          </>
        }
      </BoxModal>
    </ReactModal>
  )
}